import { useEffect, useState } from "react"
import { useDaily } from "@daily-co/daily-react"
import type { VideoQualityPreset } from "./useCallPreferences"
import { cancelVideoEffectOperations } from "./videoEffects"

/**
 * What each Prebuilt Video quality choice means for Daily: what we send (simulcast preset or
 * explicit max quality) and which layer we ask to receive from everyone (`base`).
 */
export const describeVideoQualityPreset = (preset: VideoQualityPreset) => {
  switch (preset) {
    case "high":
      return { send: { maxQuality: "high", allowAdaptiveLayers: false }, receiveLayer: 2 as const, cameraOff: false }
    case "low":
      return { send: "bandwidth-optimized" as const, receiveLayer: 0 as const, cameraOff: false }
    case "bandwidth-saver":
      return { send: "bandwidth-optimized" as const, receiveLayer: 0 as const, cameraOff: true }
    default:
      return { send: "default-video" as const, receiveLayer: "inherit" as const, cameraOff: false }
  }
}

/**
 * Applies the Video quality preference to the call object whenever it changes while in the
 * call and after each room join, since Daily clears send/receive settings when leaving a room.
 * Bandwidth saver also turns the camera off, as Prebuilt does. Per-participant receive
 * layers set by the tile cap (large calls) keep overriding the base layer.
 */
export function useVideoQuality({ preset, enabled }: { preset: VideoQualityPreset; enabled: boolean }) {
  const daily = useDaily()
  const [joinVersion, setJoinVersion] = useState(0)

  useEffect(() => {
    if (!daily) return
    const handleJoined = () => setJoinVersion((version) => version + 1)
    daily.on("joined-meeting", handleJoined)
    return () => { daily.off("joined-meeting", handleJoined) }
  }, [daily])

  useEffect(() => {
    if (!daily || !enabled) return
    let cancelled = false
    const mapping = describeVideoQualityPreset(preset)
    const apply = async () => {
      try {
        await daily.updateSendSettings({ video: mapping.send } as any)
      } catch {
        // Older Daily builds reject send settings before the first join; the receive side still applies.
      }
      if (cancelled) return
      try {
        await daily.updateReceiveSettings({ base: { video: { layer: mapping.receiveLayer } } } as any)
      } catch {
        // Best effort.
      }
      if (!cancelled && mapping.cameraOff) {
        try {
          cancelVideoEffectOperations(daily)
          daily.setLocalVideo(false)
        } catch {
          // Best effort.
        }
      }
    }
    void apply()
    return () => { cancelled = true }
  }, [daily, enabled, preset, joinVersion])
}
